import React, { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Box,
  Container,
  Paper,
  TextField,
  Button,
  Typography,
  Link,
  Alert,
  InputAdornment,
  IconButton,
  Grid,
  Stepper,
  Step,
  StepLabel,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Checkbox,
  FormControlLabel,
  FormHelperText,
} from '@mui/material';
import {
  Visibility,
  VisibilityOff,
  PersonAdd,
  ArrowBack,
  ArrowForward,
  CheckCircle,
} from '@mui/icons-material';
import axios from 'axios';
import { toast } from 'react-toastify';

const Register = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const inviteToken = searchParams.get('token');
  
  const [activeStep, setActiveStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  
  const [formData, setFormData] = useState({
    student_name: '',
    parent_name: '',
    phone: '',
    alternate_phone: '',
    email: '',
    password: '',
    confirmPassword: '',
    year_of_joining: new Date().getFullYear(),
    month_of_joining: new Date().getMonth() + 1,
  });

  const monthOptions = [
    { value: 1, label: 'January' },
    { value: 2, label: 'February' },
    { value: 3, label: 'March' },
    { value: 4, label: 'April' },
    { value: 5, label: 'May' },
    { value: 6, label: 'June' },
    { value: 7, label: 'July' },
    { value: 8, label: 'August' },
    { value: 9, label: 'September' },
    { value: 10, label: 'October' },
    { value: 11, label: 'November' },
    { value: 12, label: 'December' },
  ];
  
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [acceptedRules, setAcceptedRules] = useState(false);
  const [formErrors, setFormErrors] = useState({});

  const steps = ['Personal Information', 'Contact Details', 'Account Setup'];

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    // Clear error for this field
    if (formErrors[name]) {
      setFormErrors(prev => ({ ...prev, [name]: '' }));
    }
    setError('');
  };

  const validateStep = (step) => {
    const errors = {};
    
    if (step === 0) {
      if (!formData.student_name.trim()) {
        errors.student_name = 'Student name is required';
      }
      if (!formData.parent_name.trim()) {
        errors.parent_name = 'Parent/Guardian name is required';
      }
      if (!formData.year_of_joining) {
        errors.year_of_joining = 'Year of joining is required';
      }
      if (!formData.month_of_joining) {
        errors.month_of_joining = 'Month of joining is required';
      }
    }
    
    if (step === 1) {
      if (!formData.phone.trim()) {
        errors.phone = 'Phone number is required';
      } else if (!/^\d{10}$/.test(formData.phone.replace(/\D/g, ''))) {
        errors.phone = 'Phone number must be 10 digits';
      }
      
      if (formData.alternate_phone && !/^\d{10}$/.test(formData.alternate_phone.replace(/\D/g, ''))) {
        errors.alternate_phone = 'Alternate phone must be 10 digits';
      }
    }
    
    if (step === 2) {
      if (!formData.email.trim()) {
        errors.email = 'Email is required';
      } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
        errors.email = 'Email is invalid';
      }
      
      if (!formData.password) {
        errors.password = 'Password is required';
      } else if (formData.password.length < 6) {
        errors.password = 'Password must be at least 6 characters';
      }
      
      if (formData.password !== formData.confirmPassword) {
        errors.confirmPassword = 'Passwords do not match';
      }

      if (!acceptedRules) {
        errors.acceptedRules = 'You must read and accept the Rules and Regulations';
      }
    }
    
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleNext = () => {
    if (validateStep(activeStep)) {
      setActiveStep((prevStep) => prevStep + 1);
    }
  };

  const handleBack = () => {
    setActiveStep((prevStep) => prevStep - 1);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateStep(activeStep)) {
      return;
    }
    
    setError('');
    setLoading(true);

    try {
      const endpoint = inviteToken ? '/api/auth/register-invite' : '/api/auth/register-public';
      const payload = {
        student_name: formData.student_name,
        parent_name: formData.parent_name,
        phone: formData.phone,
        alternate_phone: formData.alternate_phone || null,
        email: formData.email,
        password: formData.password,
        year_of_joining: formData.year_of_joining,
        month_of_joining: formData.month_of_joining,
        ...(inviteToken && { invite_token: inviteToken })
      };

      const response = await axios.post(endpoint, payload);
      
      if (response.status === 201) {
        setSuccess(true);
        toast.success(response.data.message || 'Registration successful!');
        
        // Redirect to student login after 3 seconds
        setTimeout(() => {
          navigate('/student-login');
        }, 3000);
      }
    } catch (err) {
      const message = err.response?.data?.message || 'Registration failed. Please try again.';
      setError(message);
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  const renderStepContent = (step) => {
    switch (step) {
      case 0:
        return (
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Student Name"
                name="student_name"
                value={formData.student_name}
                onChange={handleChange}
                error={!!formErrors.student_name}
                helperText={formErrors.student_name}
                required
                placeholder="Enter student's full name"
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Parent/Guardian Name"
                name="parent_name"
                value={formData.parent_name}
                onChange={handleChange}
                error={!!formErrors.parent_name}
                helperText={formErrors.parent_name}
                required
                placeholder="Enter parent or guardian's name"
              />
            </Grid>
            <Grid item xs={6}>
              <FormControl fullWidth required error={!!formErrors.month_of_joining}>
                <InputLabel>Month of Joining</InputLabel>
                <Select
                  name="month_of_joining"
                  value={formData.month_of_joining}
                  onChange={handleChange}
                  label="Month of Joining"
                >
                  {monthOptions.map((m) => (
                    <MenuItem key={m.value} value={m.value}>
                      {m.label}
                    </MenuItem>
                  ))}
                </Select>
                {formErrors.month_of_joining && (
                  <Typography variant="caption" color="error" sx={{ mt: 0.5, ml: 1.5 }}>
                    {formErrors.month_of_joining}
                  </Typography>
                )}
              </FormControl>
            </Grid>
            <Grid item xs={6}>
              <FormControl fullWidth required error={!!formErrors.year_of_joining}>
                <InputLabel>Year of Joining</InputLabel>
                <Select
                  name="year_of_joining"
                  value={formData.year_of_joining}
                  onChange={handleChange}
                  label="Year of Joining"
                >
                  {Array.from({ length: new Date().getFullYear() - 2010 + 1 }, (_, i) => {
                    const year = new Date().getFullYear() - i;
                    return (
                      <MenuItem key={year} value={year}>
                        {year}
                      </MenuItem>
                    );
                  })}
                </Select>
                {formErrors.year_of_joining && (
                  <Typography variant="caption" color="error" sx={{ mt: 0.5, ml: 1.5 }}>
                    {formErrors.year_of_joining}
                  </Typography>
                )}
              </FormControl>
            </Grid>
          </Grid>
        );

      case 1:
        return (
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Primary Phone Number"
                name="phone"
                value={formData.phone}
                onChange={handleChange}
                error={!!formErrors.phone}
                helperText={formErrors.phone || 'Parent/Guardian contact number'}
                required
                placeholder="10-digit phone number"
                inputProps={{ maxLength: 15 }}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Alternate Phone Number"
                name="alternate_phone"
                value={formData.alternate_phone}
                onChange={handleChange}
                error={!!formErrors.alternate_phone}
                helperText={formErrors.alternate_phone || 'Optional secondary contact'}
                placeholder="10-digit phone number (optional)"
                inputProps={{ maxLength: 15 }}
              />
            </Grid>
          </Grid>
        );
        
      case 2:
        return (
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Email Address"
                name="email"
                type="email"
                value={formData.email}
                onChange={handleChange}
                error={!!formErrors.email}
                helperText={formErrors.email || 'This will be used for login'}
                required
                placeholder="your.email@example.com"
                autoComplete="email"
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Password"
                name="password"
                type={showPassword ? 'text' : 'password'}
                value={formData.password}
                onChange={handleChange}
                error={!!formErrors.password}
                helperText={formErrors.password || 'Minimum 6 characters'}
                required
                autoComplete="new-password"
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={() => setShowPassword(!showPassword)}
                        edge="end"
                      >
                        {showPassword ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Confirm Password"
                name="confirmPassword"
                type={showConfirmPassword ? 'text' : 'password'}
                value={formData.confirmPassword}
                onChange={handleChange}
                error={!!formErrors.confirmPassword}
                helperText={formErrors.confirmPassword}
                required
                autoComplete="new-password"
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                        edge="end"
                      >
                        {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
            
            {/* Rules and Regulations Checkbox */}
            <Grid item xs={12}>
              <Box
                sx={{
                  p: 2,
                  border: '1px solid',
                  borderColor: formErrors.acceptedRules ? 'error.main' : 'divider',
                  borderRadius: 2,
                  bgcolor: 'rgba(212, 175, 55, 0.05)',
                }}
              >
                <FormControl error={!!formErrors.acceptedRules} component="fieldset">
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={acceptedRules}
                        onChange={(e) => {
                          setAcceptedRules(e.target.checked);
                          if (formErrors.acceptedRules) {
                            setFormErrors(prev => ({ ...prev, acceptedRules: '' }));
                          }
                        }}
                        color="primary"
                      />
                    }
                    label={
                      <Typography variant="body2">
                        I have read and agree to the{' '}
                        <Link
                          href="/Guidelines.pdf"
                          target="_blank"
                          rel="noopener noreferrer"
                          sx={{ fontWeight: 600 }}
                        >
                          Rules and Regulations
                        </Link>
                        {' '}of Chinmudra Academy *
                      </Typography>
                    }
                  />
                  {formErrors.acceptedRules && (
                    <FormHelperText>{formErrors.acceptedRules}</FormHelperText>
                  )}
                </FormControl>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1, ml: 4 }}>
                  Please click on "Rules and Regulations" link above to read the document before proceeding.
                </Typography>
              </Box>
            </Grid>
          </Grid>
        );
        
      default:
        return null;
    }
  };

  if (success) {
    return (
      <Box
        sx={{
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          background: 'linear-gradient(135deg, #FAF7F2 0%, #E8DCC8 100%)',
        }}
      >
        <Container maxWidth="sm">
          <Paper
            elevation={0}
            sx={{
              p: 5,
              borderRadius: 3,
              textAlign: 'center',
              boxShadow: '0 8px 32px rgba(139, 69, 19, 0.12)',
            }}
          >
            <CheckCircle sx={{ fontSize: 80, color: 'success.main', mb: 3 }} />
            <Typography variant="h4" color="primary" gutterBottom fontWeight={700}>
              Registration Successful!
            </Typography>
            <Typography variant="body1" color="text.secondary" paragraph>
              {inviteToken
                ? 'Your account has been created successfully. You can now log in.'
                : 'Your registration has been submitted. Please wait for admin approval before logging in.'}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              Redirecting to student login page...
            </Typography>
            <Button
              variant="contained"
              onClick={() => navigate('/student-login')}
              size="large"
            >
              Go to Student Login
            </Button>
          </Paper>
        </Container>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        background: 'linear-gradient(135deg, #FAF7F2 0%, #E8DCC8 100%)',
        py: 4,
      }}
    >
      <Container maxWidth="md">
        <Paper
          elevation={0}
          sx={{
            p: 5,
            borderRadius: 3,
            boxShadow: '0 8px 32px rgba(139, 69, 19, 0.12)',
          }}
        >
          {/* Header */}
          <Box sx={{ textAlign: 'center', mb: 4 }}>
            <PersonAdd sx={{ fontSize: 64, color: 'primary.main', mb: 2 }} />
            <Typography variant="h3" color="primary" gutterBottom fontWeight={700}>
              Student Registration
            </Typography>
            <Typography variant="body1" color="text.secondary">
              {inviteToken
                ? 'Complete your registration using the invite link'
                : 'Join Chinmudra Academy'}
            </Typography>
          </Box>

          {/* Stepper */}
          <Stepper activeStep={activeStep} sx={{ mb: 4 }}>
            {steps.map((label) => (
              <Step key={label}>
                <StepLabel>{label}</StepLabel>
              </Step>
            ))}
          </Stepper>

          {error && (
            <Alert severity="error" sx={{ mb: 3, borderRadius: 2 }}>
              {error}
            </Alert>
          )}

          {/* Form */}
          <form onSubmit={activeStep === steps.length - 1 ? handleSubmit : (e) => e.preventDefault()}>
            {renderStepContent(activeStep)}

            {/* Navigation Buttons */}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 4 }}>
              <Button
                onClick={handleBack}
                disabled={activeStep === 0}
                startIcon={<ArrowBack />}
                sx={{ visibility: activeStep === 0 ? 'hidden' : 'visible' }}
              >
                Back
              </Button>

              {activeStep === steps.length - 1 ? (
                <Button
                  type="submit"
                  variant="contained"
                  size="large"
                  disabled={loading}
                  sx={{ px: 4 }}
                >
                  {loading ? 'Registering...' : 'Complete Registration'}
                </Button>
              ) : (
                <Button
                  variant="contained"
                  onClick={handleNext}
                  endIcon={<ArrowForward />}
                  size="large"
                >
                  Next
                </Button>
              )}
            </Box>
          </form>

          {/* Login Link */}
          <Box
            sx={{
              mt: 4,
              pt: 3,
              borderTop: '1px solid',
              borderColor: 'divider',
              textAlign: 'center',
            }}
          >
            <Typography variant="body2" color="text.secondary" gutterBottom>
              Already have an account?
            </Typography>
            <Link
              href="/student-login"
              variant="body2"
              color="primary"
              sx={{ textDecoration: 'none', fontWeight: 600 }}
            >
              Sign In Here
            </Link>
          </Box>
        </Paper>

        {/* Footer */}
        <Box sx={{ textAlign: 'center', mt: 3 }}>
          <Typography variant="body2" color="text.secondary">
            © 2026 Chinmudra Academy. All rights reserved.
          </Typography>
        </Box>
      </Container>
    </Box>
  );
};

export default Register;
